import {EmbedConfig} from './embedConfig';
import getThumbnail from './youtube/getThumbnail';

type ResolveMethod = (value: (PromiseLike<void> | void)) => void;
const ALL_YOUTUBE_EMBED_RESOLVES: Array<ResolveMethod> = [];
let YT_IFRAME_API_READY = false;
declare global {
    interface Window {
        YT: {
            Player: YT.Player,
            PlayerState: YT.PlayerState
        };
        onYouTubeIframeAPIReady: () => void;
    }
}

export default class YoutubeEmbedConfig extends EmbedConfig {
    protected static matcher = /(youtube\.com|youtu\.be|youtube-nocookie\.com)\/(watch\?(.*&)?v=|v\/|u\/|embed\/?)?(videoseries\?list=(.*)|[\w-]{11}|\?listType=(.*)&list=(.*))(.*)/i;
    protected idPosition = 4;
    protected api = 'https://www.youtube.com/iframe_api';

    constructor(protected url: string) {
        super(url);
        this.extractId(YoutubeEmbedConfig.matcher);
    }

    public getThumb(): Promise<string> {
        return getThumbnail(this.id);
    }

    public initPlayer(target: HTMLElement): Promise<YT.Player> {
        const detectionSpeed = 100;
        const pauseDebounce = 100;
        let pauseTimeout = 0;
        let prevTime = -1;

        // YouTube videos trigger the PAUSED state when scrubbing, making scrubbing impossible.
        const onPlayerReady: YT.PlayerEventHandler<YT.PlayerEvent> = (event) => {
            window.setInterval(() => {
                const currentTime = event.target.getCurrentTime();
                // If the time changes within a margin of 0.5 any time after pausing, assume the user is scrubbing
                if (Math.abs(currentTime - prevTime) > 0.5) {
                    clearTimeout(pauseTimeout);
                }
                prevTime = currentTime;
            }, detectionSpeed);
        };

        const onPlayerStateChange: YT.PlayerEventHandler<YT.OnStateChangeEvent> = (event: YT.OnStateChangeEvent) => {
            const {ENDED, PLAYING, PAUSED} = window.YT.PlayerState;
            clearTimeout(pauseTimeout);
            const playerState: YT.PlayerState = event.data;
            switch (playerState) {
                case PLAYING:
                    this.handlers.play();
                    break;
                case PAUSED:
                    pauseTimeout = window.setTimeout(() => this.handlers.pause(), pauseDebounce);
                    break;
                case ENDED:
                    this.handlers.pause();
                    break;
                default:
                    break;
            }
        };

        return new Promise<YT.Player>((resolve) => {
            this.loadApi().then(() => {
                const player: YT.Player = new window.YT.Player(target, {
                    videoId: <string>this.id,
                    host: 'https://www.youtube-nocookie.com',
                    events: {
                        onReady: onPlayerReady,
                        onStateChange: onPlayerStateChange,
                    },
                    playerVars: {
                        rel: 0,
                    }
                });

                this.actions.play = () => player.playVideo();
                this.actions.pause = () => player.pauseVideo();

                resolve(player);
            });
        });
    }

    protected loadApi(): Promise<void> {
        return new Promise<void>((resolve: ResolveMethod) => {
            ALL_YOUTUBE_EMBED_RESOLVES.push(resolve);

            window.onYouTubeIframeAPIReady = () => {
                YT_IFRAME_API_READY = true;
                ALL_YOUTUBE_EMBED_RESOLVES.forEach(
                    (resolveToCall: ResolveMethod) => resolveToCall()
                );
            };

            this.addScript(() => {
                return !!window.YT;
            }).finally(() => {
                if (YT_IFRAME_API_READY) {
                    resolve();
                }
            });
        });
    }
}
